import animalHash from "angry-purple-tiger";
import { capitalize, get } from "lodash";
import moment from "moment";
import Tooltip from "rc-tooltip";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import CenteredLoading from "../../../../components/CenteredLoading";
import ExternalLink from "../../../../components/ExternalLink";
import TwitterIcon from "../../../../components/icons/TwitterIcon";
import InfoItems from "../../../../components/InfoItems";
import { useGoogleAnalytics } from "../../../../context/googleAnalytics";
import {
  HotspotMakerAddressKey,
  hotspotMakerMetadata,
  useHotspotMakers
} from "../../../../context/hotspotMakers/HotspotMakersProvider";
import { useHotspots } from "../../../../context/hotspots";
import { useWorkspaces } from "../../../../context/workspaces";
import { ApiResponse, fetchApi } from "../../../../utils/api";
import classNames from "../../../../utils/classNames";
import { Hotspot } from "../../../../utils/types";
import { renderDate } from "../HotspotInfoTab";

interface Props {
  className?: string;
  hotspot: Hotspot;
}

const RealTimeHotspotDiagnostics: React.FC<Props> = ({
  className,
  hotspot
}) => {
  const { trackEvent } = useGoogleAnalytics();
  const { currentWorkspace } = useWorkspaces();
  const { getHotspotMakerName } = useHotspotMakers();
  const [diagnostics, setDiagnostics] = useState<RemoteDiagnostics | null>(
    null
  );
  const { setCurrenHotspotId } = useHotspots();
  const [showDiagnostics, setShowDiagnostics] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    return () => {
      setDiagnostics(null);
    };
  }, []);

  useEffect(() => {
    if (hotspot.id) {
      setShowDiagnostics(false);
      setDiagnostics(null);
    }
  }, [hotspot.id]);

  const fetchDiagnostics = useCallback(async () => {
    if (diagnostics) {
      setShowDiagnostics(true);

      return;
    }

    try {
      setLoading(true);

      const response = await fetchApi(
        `api/workspace/${currentWorkspace?.uuid}/hotspot/${hotspot.id}/maker-data`
      );

      const apiResponse: ApiResponse<RemoteDiagnostics> = await response.json();

      setLoading(false);

      if (apiResponse.status) {
        setDiagnostics(apiResponse.data);
      }

      setShowDiagnostics(true);
    } catch {
      setLoading(false);
    }
  }, [currentWorkspace?.uuid, diagnostics, hotspot.id]);

  const currentMaker = useMemo(
    () => get(hotspotMakerMetadata, [hotspot.payer]),
    [hotspot.payer]
  );

  const makerTwitterHandle = useMemo(() => {
    if (
      [
        HotspotMakerAddressKey.heliumOG,
        HotspotMakerAddressKey.heliumInc
      ].includes(hotspot.payer as HotspotMakerAddressKey)
    ) {
      return;
    }

    const makerId =
      hotspot.payer === HotspotMakerAddressKey.calChipConnectedDevices
        ? HotspotMakerAddressKey.rakWireless
        : hotspot.payer;

    const twitterLink =
      get(hotspotMakerMetadata, [makerId, "twitterLink"]) || "";

    const twitterHandle = twitterLink.replace("https://twitter.com/", "");

    return twitterHandle;
  }, [hotspot.payer]);

  const openTwitter = useCallback(() => {
    trackEvent("tweet maker for hotspot diagnostics", {
      event_category: "tweet makers",
      event_label: makerTwitterHandle
    });

    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
      `I'd love to see @${makerTwitterHandle} work with @hotspotty on making remote diagnostics available for my @helium hotspot! 🎈 https://blog.hotspotty.net/maker-partnership-program`
    )}`;

    window.open(url, "_blank", "noopener,noreferrer");
  }, [makerTwitterHandle, trackEvent]);

  const renderRequestAccess = useMemo(
    () => (
      <>
        <p className="mt-2 max-w-4xl text-sm text-gray-500 dark:text-gray-300">
          With the{" "}
          <ExternalLink
            className="inline-flex"
            text="upgrade to Light Hotspots"
            url="https://blog.helium.com/light-hotspots-explained-everything-you-need-to-know-f86612f571c6"
          />
          , we no longer have access to the IP address to ping the hotspot for
          real time status checking. We are{" "}
          <ExternalLink
            className="inline-flex"
            text="working directly with hotspot Makers"
            url="https://blog.hotspotty.net/maker-partnership-program/"
          />{" "}
          on an integration that brings real-time hotspot diagnostics back to
          Hotspotty. Please ask your hotspot Maker for this integration so we
          can bring you a better diagnostics experience.
        </p>

        {makerTwitterHandle && (
          <button
            type="button"
            onClick={openTwitter}
            className="mt-4 justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-hotspotty-400 hover:bg-hotspotty-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hotspotty-400 flex items-center disabled:opacity-50 disabled:cursor-default disabled:hover:bg-hotspotty-400"
          >
            <TwitterIcon className="h-4 w-4 mr-2" aria-hidden="true" />
            <span>Request Access</span>
          </button>
        )}
      </>
    ),
    [makerTwitterHandle, openTwitter]
  );

  const renderInternetHealth = useMemo(() => {
    const colorsMap: { [key: string]: string } = {
      "0": "bg-red-600",
      "1": "bg-red-400",
      "2": "bg-orange-400",
      "3": "bg-yellow-300",
      "4": "bg-green-400",
      "5": "bg-green-600"
    };

    const internetSignalRepresentation = (value: number) => {
      if (value === 0) return "Fail";
      if (value < 3) return "Weak";
      return "Good";
    };

    return (
      <div className="flex items-center">
        {diagnostics?.inet24hconnstatus.map((item, index) => (
          <Tooltip
            overlay={
              <>
                <div>{renderDate(Number(item[2]))}</div>
                <div>
                  Internal connection:{" "}
                  {internetSignalRepresentation(Number(item[0]))}
                </div>
                <div>
                  External connection:{" "}
                  {internetSignalRepresentation(Number(item[1]))}
                </div>
              </>
            }
          >
            <span
              key={index}
              className={classNames("flex w-3 h-16 mr-0.5", colorsMap[item[1]])}
            />
          </Tooltip>
        ))}
      </div>
    );
  }, [diagnostics?.inet24hconnstatus]);

  const renderCpuTemperature = useMemo(() => {
    const { cputemp } = diagnostics || { cpuTemp: 0 };
    const cputTempFormatted = Number(cputemp);
    let color = "bg-red-600";

    if (cputTempFormatted >= 60 && cputTempFormatted < 80)
      color = "bg-yellow-500";

    if (cputTempFormatted < 60) color = "bg-green-600";

    return (
      <div className="flex items-center">
        <div className="w-full h-6 bg-gray-500 dark:bg-gray-800 overflow-hidden rounded-lg">
          <div
            style={{ width: `${cputTempFormatted}%` }}
            className={classNames(color, "h-full")}
          ></div>
        </div>
        <span className="text-white font-bold ml-4 text-base">
          {Number(diagnostics?.cputemp).toFixed(2)}°C
        </span>
      </div>
    );
  }, [diagnostics]);

  const diagnosticsData = useMemo(
    () => [
      {
        title: "Connectivity",
        items: [
          {
            label: "Connected validator",
            tooltip:
              diagnostics?.validatorconntime &&
              `Connected since ${moment(
                diagnostics?.validatorconntime
              ).fromNow()}`,
            value: diagnostics?.validator && (
              <ExternalLink
                className="inline-flex"
                iconStyle={{ width: 20, height: 20 }}
                text={animalHash(diagnostics.validator)}
                url={`https://hntscan.io/blockchain/validators/${diagnostics.validator}`}
              />
            )
          },
          {
            label: "Last LoRa Packet received",
            value: moment(diagnostics?.lastlorarecvd).fromNow()
          },
          {
            label: "Internet connection health history",
            value: renderInternetHealth
          },
          {
            label: "Internet connection type",
            value: capitalize(diagnostics?.inetconntype)
          }
        ]
      },
      {
        title: "Hardware",
        items: [
          {
            label: "Maker",
            value: (
              <Link
                className="hover:underline"
                onClick={() => setCurrenHotspotId("")}
                to={`/hotspots/makers/${hotspot.payer}/statistics`}
              >
                {currentMaker.name}
              </Link>
            )
          },
          {
            label: "Model",
            value: diagnostics?.model?.split("_").map(capitalize).join(" ")
          },
          {
            label: "RAM",
            value: `${diagnostics?.ram}MB`
          },
          {
            label: "SD card",
            value: diagnostics?.sdcard,
            tooltip: (diagnostics?.sdcard || "").includes("ERROR") ? (
              <>
                The SD card is faulty and is read only right now. It can not
                store new data.{" "}
                <ExternalLink
                  className="inline-flex"
                  text="Click here"
                  url="https://support.getmntd.com/hc/en-us/articles/6832177438999-Replacing-the-SD-card"
                />{" "}
                to learn how to solve this issue.
              </>
            ) : undefined
          },
          {
            label: "MAC ethernet",
            value: diagnostics?.maceth0
          },
          {
            label: "MAC WiFi",
            value: diagnostics?.macwlan0
          },
          {
            label: "CPU Temperature",
            value: renderCpuTemperature
          }
        ]
      },
      {
        title: "Software",
        items: [
          {
            label: "Miner version",
            value: diagnostics?.minerversion
          },
          {
            label: "Firmware version",
            value: diagnostics?.fwversion
          }
        ]
      }
    ],
    [
      currentMaker.name,
      diagnostics?.fwversion,
      diagnostics?.inetconntype,
      diagnostics?.lastlorarecvd,
      diagnostics?.maceth0,
      diagnostics?.macwlan0,
      diagnostics?.minerversion,
      diagnostics?.model,
      diagnostics?.ram,
      diagnostics?.sdcard,
      diagnostics?.validator,
      diagnostics?.validatorconntime,
      hotspot.payer,
      renderCpuTemperature,
      renderInternetHealth,
      setCurrenHotspotId
    ]
  );

  const renderRemoteDiagnostics = useMemo(() => {
    if (!showDiagnostics) {
      return (
        <>
          <p className="mt-2 max-w-4xl text-sm text-gray-500 dark:text-gray-300">
            This hotspot supports remote diagnostics via the{" "}
            <Link
              onClick={() => setCurrenHotspotId("")}
              className="inline-flex hover:text-gray-600 font-semibold dark:hover:text-white dark:hover:underline"
              to={`/hotspots/makers/${hotspot.payer}/statistics`}
            >
              {getHotspotMakerName(hotspot.payer)}
            </Link>{" "}
            API integration as part of the{" "}
            <ExternalLink
              className="inline-flex"
              text="Hotspotty Maker Partnership Program"
              url="https://blog.hotspotty.net/maker-partnership-program/"
            />
            .
          </p>
          <div className="mt-6">
            <button
              onClick={fetchDiagnostics}
              className="dark:text-white bg-transparent border border-gray-300 rounded-md py-2 px-4 text-sm font-medium flex items-center focus:outline-none hover:bg-gray-500 hover:text-white dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-default disabled:hover:bg-transparent"
              disabled={loading}
            >
              {loading && (
                <CenteredLoading className="w-4 h-4 mr-2" aria-hidden="true" />
              )}
              <span>Show diagnostics</span>
            </button>
          </div>
        </>
      );
    }

    return (
      <>
        {diagnostics ? (
          <>
            <p className="mt-2 max-w-4xl text-sm text-gray-500 dark:text-gray-300">
              The remote hotspot diagnostics information below is brought to you
              trough the{" "}
              <Link
                className="inline-flex hover:text-gray-600 font-semibold dark:hover:text-white dark:hover:underline"
                to={`/hotspots/makers/${hotspot.payer}/statistics`}
              >
                {getHotspotMakerName(hotspot.payer)}
              </Link>{" "}
              API integration as part of the{" "}
              <ExternalLink
                className="inline-flex"
                text="Hotspotty Maker Partnership Program"
                url="https://blog.hotspotty.net/maker-partnership-program/"
              />{" "}
              and was retrieved directly from the hotspot{" "}
              {moment.unix(diagnostics?.timestamp || 0).fromNow()}.
            </p>
            <div className="mt-4">
              {diagnosticsData.map((section, index) => (
                <div
                  className="py-4 border-b border-gray-800 last:border-0 mb-4"
                  key={index}
                >
                  <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white flex items-center mb-2">
                    {section.title}
                  </h3>

                  <InfoItems infoItems={section.items} className="mt-4" />
                </div>
              ))}
            </div>
          </>
        ) : (
          <p className="mt-2 max-w-4xl text-sm text-gray-500 dark:text-gray-300">
            We currently don't have information for this hotspot as it hasn't
            checked in with the{" "}
            <Link
              className="inline-flex hover:text-gray-600 font-semibold dark:hover:text-white dark:hover:underline"
              to={`/hotspots/makers/${hotspot.payer}/statistics`}
            >
              {getHotspotMakerName(hotspot.payer)}
            </Link>{" "}
            recently. Possible causes: Hotpot powered off or on a bad Internet
            connection. Check local diagnostics.
          </p>
        )}

        <div className="mt-6 flex w-full">
          <button
            onClick={() => {
              setShowDiagnostics(false);
            }}
            className="dark:text-white bg-transparent border border-gray-300 rounded-md py-2 px-4 text-sm font-medium flex items-center focus:outline-none hover:bg-gray-500 hover:text-white dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-default disabled:hover:bg-transparent"
            disabled={loading}
          >
            Hide diagnostics
          </button>
        </div>
      </>
    );
  }, [
    diagnostics,
    diagnosticsData,
    fetchDiagnostics,
    getHotspotMakerName,
    hotspot.payer,
    loading,
    setCurrenHotspotId,
    showDiagnostics
  ]);

  return (
    <div className={className}>
      <div className="flex pb-5 w-full justify-between border-b border-gray-200 dark:border-gray-500">
        <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white flex items-center">
          Remote diagnostics
        </h3>
      </div>

      {currentMaker.remoteDiagnostics
        ? renderRemoteDiagnostics
        : renderRequestAccess}
    </div>
  );
};

export default RealTimeHotspotDiagnostics;

//
// Utils
//

interface RemoteDiagnostics {
  cputemp: string;
  fwversion: string;
  inet24hconnstatus: string[][];
  inetconntype: string;
  lastfactoryreset: string;
  lastlorarecvd: string;
  maceth0: string;
  macwlan0: string;
  minerversion: string;
  model: string;
  ram: string;
  report: string;
  reportvia: string;
  rpirevision: string;
  rpiserial: string;
  sdcard: string;
  sn: string;
  timestamp: number;
  validator: string;
  validatorconntime: string;
}
